"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { useSendBrandSms, useVerifyBrandSms } from "@/features/admin/queries/useBrandPasswordReset";

const TIMER_SECONDS = 180;

export function useBrandSmsVerification() {
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [timer, setTimer] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sendSmsMutation = useSendBrandSms();
  const verifySmsMutation = useVerifyBrandSms();

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    setIsExpired(false);
    setTimer(TIMER_SECONDS);
    const id = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          intervalRef.current = null;
          setIsExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    intervalRef.current = id;
  }, [clearTimer]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const sendSms = useCallback(() => {
    if (sendSmsMutation.isPending) return;
    sendSmsMutation.mutate(
      { email: email.trim().toLowerCase(), phoneNumber },
      {
        onSuccess: () => {
          startTimer();
        },
        onError: (err) => {
          const status =
            typeof err === "object" && err !== null && "status" in err
              ? (err as { status?: number }).status
              : undefined;
          toast.error(
            status === 400
              ? "등록된 브랜드 이메일과 휴대폰 번호를 확인해주세요."
              : "인증번호 발송에 실패했습니다.",
          );
        },
      },
    );
  }, [email, phoneNumber, sendSmsMutation, startTimer]);

  const verifySms = useCallback(() => {
    if (verifySmsMutation.isPending) return;
    verifySmsMutation.mutate(
      { email: email.trim().toLowerCase(), phoneNumber, code },
      {
        onSuccess: () => {
          setIsVerified(true);
          clearTimer();
        },
        onError: () => {
          toast.error("인증번호를 확인해주세요.");
        },
      },
    );
  }, [email, phoneNumber, code, verifySmsMutation, clearTimer]);

  return {
    email,
    setEmail,
    phoneNumber,
    setPhoneNumber,
    code,
    setCode,
    isVerified,
    isExpired,
    timer,
    isSending: sendSmsMutation.isPending,
    isVerifying: verifySmsMutation.isPending,
    sendSms,
    verifySms,
  };
}
